import React from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import { obterTodasEmpresas } from '../../../data/empresas';
import { estaAberto } from '../../../utils/storeHours';

const FeaturedStoresSection = () => {
  // Obter as primeiras 4 empresas como destaque
  const empresas = obterTodasEmpresas();
  const featuredStores = empresas.slice(0, 4).map(empresa => ({
    id: empresa.id,
    name: empresa.nome,
    category: empresa.tipo,
    description: empresa.descricao,
    image: empresa.imagem,
    logo: empresa.logo,
    logoSecundario: empresa.logoSecundario,
    rating: empresa.avaliacao,
    reviews: empresa.numeroAvaliacoes,
    isOpen: estaAberto(empresa.horarioFuncionamento),
    horarioFuncionamento: empresa.horarioFuncionamento,
    specialOffer: empresa.promocao?.titulo || "Oferta especial disponível",
    tags: empresa.tags || empresa.servicos.slice(0, 3),
    phone: empresa.telefone
  }));

  return (
    <section className="py-16 lg:py-24 bg-muted/30">
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12">
          <div className="mb-6 lg:mb-0">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              Lojas
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl">
              Conheça alguns dos estabelecimentos da Estação Marina da Rocha
            </p>
          </div>
          <Link to="/store-directory">
            <Button variant="outline" size="lg" className="font-semibold">
              Ver Todas as Lojas
              <Icon name="ArrowRight" size={20} className="ml-2" />
            </Button>
          </Link>
        </div>

        {/* Featured Stores Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {featuredStores?.map((store) => (
            <div
              key={store?.id}
              className="bg-card rounded-xl border border-border overflow-hidden hover:shadow-warm-lg transition-all duration-300"
            >
              {/* Store Image */}
              <div className="relative h-48 overflow-hidden group bg-muted/50 rounded-t-xl p-0.5">
                <Image
                  src={store?.image || "/assets/images/no_image.png"}
                  alt={`Foto ${store?.name}`}
                  className="w-full h-full object-cover rounded-t-lg"
                />
                
                {/* Status Badge */}
                <div className="absolute top-4 left-4 z-20">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium backdrop-blur-sm ${
                    store?.isOpen 
                      ? 'bg-green-100/90 text-green-700 border border-green-200' :'bg-red-100/90 text-red-700 border border-red-200'
                  }`}>
                    {store?.isOpen ? 'Aberto' : 'Fechado'}
                  </span>
                </div>

              </div>

              {/* Store Content */}
              <div className="p-6">
                {/* Header */}
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h3 className="text-xl font-semibold text-foreground mb-1">
                      {store?.name}
                    </h3>
                    <span className="text-sm text-muted-foreground bg-muted px-2 py-1 rounded-full">
                      {store?.category}
                    </span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <Icon name="Star" size={16} className="text-yellow-500 fill-current" />
                    <span className="text-sm font-medium text-foreground">{store?.rating}</span>
                    <span className="text-sm text-muted-foreground">({store?.reviews})</span>
                  </div>
                </div>

                {/* Description */}
                <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
                  {store?.description}
                </p>

                {/* Special Offer */}
                {store?.specialOffer && (
                  <div className="bg-accent/10 border border-accent/20 rounded-lg p-3 mb-4">
                    <div className="flex items-center space-x-2">
                      <Icon name="Tag" size={16} className="text-accent" />
                      <span className="text-sm font-medium text-accent-foreground">
                        {store?.specialOffer}
                      </span>
                    </div>
                  </div>
                )}

                {/* Tags */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {store?.tags?.map((tag, index) => (
                    <span
                      key={index}
                      className="text-xs bg-muted text-muted-foreground px-2 py-1 rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between">
                  <Link
                    to={`/store-detail?id=${store?.id}`}
                    className="text-primary font-medium text-sm hover:text-primary/80 transition-colors duration-200 flex items-center"
                  >
                    Ver Detalhes
                    <Icon name="ArrowRight" size={16} className="ml-1" />
                  </Link>
                  
                  <div className="flex items-center space-x-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-muted-foreground hover:text-foreground"
                    >
                      <Icon name="Phone" size={16} />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Call to Action */}
        <div className="text-center mt-12">
          <p className="text-muted-foreground mb-4">
            Descubra mais de {empresas.length} estabelecimentos únicos
          </p>
          <Link to="/store-directory">
            <Button variant="default" size="lg" className="font-semibold px-8">
              Explorar Diretório Completo
              <Icon name="Store" size={20} className="ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default FeaturedStoresSection;